import React from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import { X, Clock, AlertTriangle, Copy } from "lucide-react";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  surfaceMuted,
  badgeBase,
  dividerInner,
  iconSize,
  motionFadeUp,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const AttendanceSaveModal = ({
  isOpen,
  onClose,
  onConfirm,
  onDuplicate,
  currentDate,
  currentTime,
  branch,
  presentCount,
  absentCount,
  totalStudents,
  savedTimes,
  currentSessionTimes,
  isDuplicate,
}) => {
  if (!isOpen) return null;

  const formatDateForDisplay = (dateString) => {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const attendancePercentage =
    totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0;

  const presentPct = totalStudents > 0 ? (presentCount / totalStudents) * 100 : 0;
  const absentPct = totalStudents > 0 ? (absentCount / totalStudents) * 100 : 0;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        {...motionFadeUp}
        className={cn(
          card,
          "relative w-full max-w-lg max-h-[95vh] flex flex-col shadow-xl mx-4"
        )}
      >
        {/* Header */}
        <div className={cn("px-5 pt-5 pb-4 border-b", dividerInner)}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={cardTitle}>Save Attendance</h2>
              <p className={cn(bodySecondary, "mt-0.5")}>
                Review before saving
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
            >
              <X className={iconSize.lg} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Session Info — current + duplicated sessions */}
          <div
            className={cn(
              surfaceMuted,
              "p-4 rounded-sm border space-y-3",
              dividerInner
            )}
          >
            <div className="flex items-center gap-3">
              <Clock className={cn(iconSize.lg, "text-accent1 flex-shrink-0")} />
              <div className="flex-1 min-w-0">
                <p className={microLabel}>Session</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">
                  {formatDateForDisplay(currentDate)}
                </p>
              </div>
              {branch && (
                <span className={cn(badgeBase, "bg-accent1/10 text-accent1 border-accent1/30 flex-shrink-0")}>
                  {branch}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {currentSessionTimes.map((time, index) => (
                <span
                  key={index}
                  className={cn(
                    badgeBase,
                    time === currentTime
                      ? "bg-accent1/10 text-accent1 border-accent1/30"
                      : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700",
                    "inline-flex items-center gap-1"
                  )}
                >
                  <Clock className={iconSize.xs} />
                  {time}
                  {time === currentTime && (
                    <span className="text-[10px] font-bold uppercase ml-0.5">Active</span>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Duplicate Warning */}
          {isDuplicate && (
            <div className="flex items-start gap-3 p-3.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm">
              <AlertTriangle className={cn(iconSize.md, "text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0")} />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Updating existing record
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  This session was previously saved
                </p>
              </div>
            </div>
          )}

          {/* Attendance Summary */}
          <div className="space-y-3">
            <p className={microLabel}>Attendance Summary</p>

            <div className="grid grid-cols-2 gap-3">
              {/* Present */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-sm p-4">
                <p className={cn(microLabel, "text-emerald-700 dark:text-emerald-400")}>
                  Present
                </p>
                <p className="text-2xl font-black text-emerald-700 dark:text-emerald-300 mt-1">
                  {presentCount}
                </p>
                <div className="mt-2 h-1 bg-emerald-200 dark:bg-emerald-800 rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 dark:bg-emerald-500 rounded-sm"
                    style={{ width: `${presentPct}%` }}
                  />
                </div>
              </div>

              {/* Absent */}
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm p-4">
                <p className={cn(microLabel, "text-red-700 dark:text-red-400")}>
                  Absent
                </p>
                <p className="text-2xl font-black text-red-700 dark:text-red-300 mt-1">
                  {absentCount}
                </p>
                <div className="mt-2 h-1 bg-red-200 dark:bg-red-800 rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-red-600 dark:bg-red-500 rounded-sm"
                    style={{ width: `${absentPct}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Total */}
            <div
              className={cn(
                surfaceMuted,
                "flex items-center justify-between p-3 rounded-sm border",
                dividerInner
              )}
            >
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Total Students
              </span>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {totalStudents}
                </span>
                <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
                <span className="text-sm font-bold text-accent1">
                  {attendancePercentage}% Present
                </span>
              </div>
            </div>
          </div>

          {/* Other Sessions Today — previously saved (backend) */}
          <div className="space-y-3">
            <p className={microLabel}>Other Sessions Today</p>
            <div
              className={cn(
                surfaceMuted,
                "p-3.5 rounded-sm border min-h-[48px]",
                dividerInner
              )}
            >
              {savedTimes.length === 0 ? (
                <p className={cn(bodySecondary, "text-center py-2")}>
                  No previously saved sessions
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {savedTimes.map((time, index) => (
                    <span
                      key={index}
                      className={cn(
                        badgeBase,
                        "bg-white dark:bg-gray-800 inline-flex items-center gap-1"
                      )}
                    >
                      <Clock className={iconSize.xs} />
                      {time}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={cn(
            surfaceMuted,
            "flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 px-5 py-4 border-t",
            dividerInner
          )}
        >
          <button
            onClick={onDuplicate}
            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border border-blue-300 dark:border-blue-700 rounded-sm text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <Copy className={iconSize.md} />
            Duplicate
          </button>

          <div className="flex-1" />

          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            className="px-4 py-2.5 text-sm font-medium bg-accent1 hover:bg-accent1/90 text-white rounded-sm transition-colors"
          >
            Save Attendance
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default AttendanceSaveModal;
